import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * Hook for Hyperliquid trade history (fills).
 *
 * Listens for `solcheddar-hl-fills` window events (dispatched by the
 * WS layer when it receives `{type: 'hl_fills', data: [...]}` from the backend).
 *
 * Triggers a fetch by dispatching `solcheddar-hl-fills-request`, which
 * server_swaps.py should forward as `hl_fills` to hyperliquid_trading.
 */
export default function useHlFills({ enabled = true, autoRefreshMs = 30000 } = {}) {
  const [fills, setFills] = useState([])
  const [loading, setLoading] = useState(false)
  const lastFetchRef = useRef(0)

  const refresh = useCallback(() => {
    if (!enabled) return
    setLoading(true)
    lastFetchRef.current = Date.now()
    window.dispatchEvent(new CustomEvent('solcheddar-hl-fills-request', { detail: { limit: 200 } }))
  }, [enabled])

  // Receive fills from WS.
  //  - `solcheddar-hl-fills`  (full list): authoritative history refresh.
  //  - `solcheddar-hl-fill`   (single):    a provisional fill pushed the instant
  //    an order returns, or a real-time fill pushed by the userFills WS.
  // Full-list refreshes MERGE rather than replace, so a provisional fill that
  // HL hasn't registered yet survives until the authoritative fill for the same
  // oid lands — then the provisional is dropped (superseded). No flicker.
  useEffect(() => {
    const sortDesc = (arr) => arr.sort((a, b) => (b.time || 0) - (a.time || 0))

    const mergeFull = (list) => {
      const auth = Array.isArray(list) ? list : []
      const authOids = new Set(auth.map(f => String(f.oid)))
      setFills(prev => {
        const keepProv = prev.filter(f => f._provisional && !authOids.has(String(f.oid)))
        return sortDesc([...keepProv, ...auth])
      })
      setLoading(false)
    }

    const addOne = (fill) => {
      if (!fill) return
      setFills(prev => {
        // drop any earlier provisional for this oid; keep authoritative entries
        const rest = prev.filter(f => !(f._provisional && String(f.oid) === String(fill.oid)))
        // if an authoritative fill for this oid already exists, ignore the provisional
        if (fill._provisional && rest.some(f => !f._provisional && String(f.oid) === String(fill.oid))) return rest
        return sortDesc([fill, ...rest])
      })
    }

    const fullHandler = (e) => mergeFull(e.detail)
    const oneHandler = (e) => addOne(e.detail)
    window.addEventListener('solcheddar-hl-fills', fullHandler)
    window.addEventListener('solcheddar-hl-fill', oneHandler)
    return () => {
      window.removeEventListener('solcheddar-hl-fills', fullHandler)
      window.removeEventListener('solcheddar-hl-fill', oneHandler)
    }
  }, [])

  // Initial + periodic refresh while enabled
  useEffect(() => {
    if (!enabled) {
      setFills([])
      return
    }
    refresh()
    if (!autoRefreshMs) return
    const id = setInterval(refresh, autoRefreshMs)
    return () => clearInterval(id)
  }, [enabled, autoRefreshMs, refresh])

  // Refresh after we place / close a trade
  useEffect(() => {
    if (!enabled) return
    const handler = () => {
      // Small delay so HL has time to register the fill
      setTimeout(refresh, 1500)
    }
    window.addEventListener('solcheddar-hl-order-placed', handler)
    window.addEventListener('solcheddar-hl-close-position', handler)
    return () => {
      window.removeEventListener('solcheddar-hl-order-placed', handler)
      window.removeEventListener('solcheddar-hl-close-position', handler)
    }
  }, [enabled, refresh])

  return { fills, loading, refresh }
}